/**
 * DialogSolicitarCompra - Modal para criar cotação a partir da ficha técnica
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { CurrencyInput } from '../ui/currency-input';
import { ShoppingCart, Package, AlertTriangle, Building2, Users, Loader2 } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const DialogSolicitarCompra = ({
  open,
  onClose,
  item,
  projeto,
  embarcacaoIndex,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [empresas, setEmpresas] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  
  const [formData, setFormData] = useState({
    empresa_id: '',
    titulo: '',
    descricao: '',
    quantidade: 1,
    unidade: 'UN',
    valor_estimado: '',
    fornecedores_ids: [],
    urgente: false
  });

  useEffect(() => {
    if (open) {
      fetchEmpresas();
      fetchFornecedores();
      
      // Preencher dados do item
      if (item) {
        const produtoVinculado = item.itens_custo?.find(ic => ic.tipo === 'produto');
        setFormData(prev => ({
          ...prev,
          titulo: `Cotação - ${item.descricao || item.item}`,
          descricao: item.observacao || '',
          quantidade: parseFloat(item.quantidade) || 1,
          valor_estimado: produtoVinculado?.valor_unitario || ''
        }));
      }
    }
  }, [open, item]);

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      const data = response.data;
      // API pode retornar {items: []} ou array direto
      const empresasList = Array.isArray(data) ? data : (data?.items || []);
      setEmpresas(empresasList);
      // Selecionar primeira empresa por padrão
      if (empresasList.length > 0 && !formData.empresa_id) {
        setFormData(prev => ({ ...prev, empresa_id: empresasList[0].id }));
      }
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
      setEmpresas([]);
    }
  };

  const fetchFornecedores = async () => {
    try {
      const response = await api.get('/fornecedores');
      const data = response.data;
      // API pode retornar {items: []} ou array direto
      const fornecedoresList = Array.isArray(data) ? data : (data?.items || []);
      setFornecedores(fornecedoresList);
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error);
    }
  };

  const toggleFornecedor = (fornecedorId) => {
    setFormData(prev => ({
      ...prev,
      fornecedores_ids: prev.fornecedores_ids.includes(fornecedorId)
        ? prev.fornecedores_ids.filter(id => id !== fornecedorId)
        : [...prev.fornecedores_ids, fornecedorId]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.empresa_id) {
      toast.error('Selecione uma empresa');
      return;
    }
    if (!formData.titulo.trim()) {
      toast.error('Informe um título para a cotação');
      return;
    }
    if (formData.fornecedores_ids.length === 0) {
      toast.error('Selecione pelo menos um fornecedor');
      return;
    }

    setLoading(true);
    try {
      const empresa = empresas.find(e => e.id === formData.empresa_id);
      const produtoVinculado = item?.itens_custo?.find(ic => ic.tipo === 'produto');
      
      const cotacaoData = {
        empresa_id: formData.empresa_id,
        empresa_razao_social: empresa?.razao_social || empresa?.nome_fantasia || '',
        empresa_cnpj: empresa?.cnpj || '',
        titulo: formData.titulo,
        descricao: formData.descricao,
        projeto_id: projeto?.id || '',
        projeto_nome: projeto?.name || '',
        embarcacao_index: embarcacaoIndex ? parseInt(embarcacaoIndex) : null,
        status: 'rascunho',
        itens: [{
          id: crypto.randomUUID(),
          tipo: 'produto',
          // Priorizar nome do produto vinculado sobre o nome do item do checklist
          descricao: produtoVinculado?.produto_nome || item?.descricao || item?.item || formData.titulo,
          produto_id: produtoVinculado?.produto_id || null,
          produto_nome: produtoVinculado?.produto_nome || null,
          quantidade: parseFloat(formData.quantidade) || 1,
          unidade: formData.unidade,
          valor_estimado: parseFloat(formData.valor_estimado) || 0,
          especificacoes: item?.observacao || '',
          // Guardar referência ao item do checklist para rastreabilidade
          checklist_item_nome: item?.descricao || item?.item || null
        }],
        fornecedores: formData.fornecedores_ids.map(fornId => {
          const forn = fornecedores.find(f => f.id === fornId);
          return {
            fornecedor_id: fornId,
            fornecedor_nome: forn?.razao_social || forn?.nome || '',
            contato_nome: forn?.contato_nome || '',
            contato_telefone: forn?.telefone || '',
            contato_email: forn?.email || '',
            status: 'pendente'
          };
        }),
        observacao: formData.urgente ? '⚠️ URGENTE - Prioridade alta' : '',
        urgente: formData.urgente, // Flag para exibir badge
        origem: 'ficha_tecnica',
        ficha_tecnica_item_id: item?.id || null
      };

      const response = await api.post('/cotacoes', cotacaoData);
      
      toast.success('Cotação criada com sucesso!');
      onSuccess?.(response.data);
      onClose();
    } catch (error) {
      console.error('Erro ao criar cotação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar cotação');
    } finally {
      setLoading(false);
    }
  };

  const produtoVinculado = item?.itens_custo?.find(ic => ic.tipo === 'produto');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-blue-600" />
            Solicitar Compra
          </DialogTitle>
          <DialogDescription>
            Criar cotação vinculada ao projeto {projeto?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Alerta de estoque insuficiente */}
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Estoque insuficiente</p>
                <p className="text-sm text-yellow-700">
                  O item &ldquo;{item?.descricao || item?.item}&rdquo; não possui estoque disponível. 
                  Crie uma cotação para solicitar a compra.
                </p>
              </div>
            </div>
          </div>

          {/* Info do item */}
          {produtoVinculado && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Package className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-800">Produto vinculado</span>
              </div>
              <p className="text-sm text-blue-700">{produtoVinculado.produto_nome}</p>
              <p className="text-xs text-blue-600">
                Custo médio: {(produtoVinculado.valor_unitario || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </p>
            </div>
          )}

          {/* Empresa */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Empresa Solicitante *
            </Label>
            <Select
              value={formData.empresa_id}
              onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a empresa" />
              </SelectTrigger>
              <SelectContent>
                {empresas.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.nome_fantasia || emp.razao_social}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Título */}
          <div className="space-y-2">
            <Label>Título da Cotação *</Label>
            <Input
              value={formData.titulo}
              onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
              placeholder="Ex: Cotação Motor Yamaha 150HP"
            />
          </div>

          {/* Quantidade e Unidade */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Quantidade *</Label>
              <Input
                type="number"
                min="1"
                value={formData.quantidade}
                onChange={(e) => setFormData(prev => ({ ...prev, quantidade: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Unidade</Label>
              <Select
                value={formData.unidade}
                onValueChange={(v) => setFormData(prev => ({ ...prev, unidade: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UN">UN - Unidade</SelectItem>
                  <SelectItem value="PC">PC - Peça</SelectItem>
                  <SelectItem value="KG">KG - Quilograma</SelectItem>
                  <SelectItem value="M">M - Metro</SelectItem>
                  <SelectItem value="M2">M² - Metro Quadrado</SelectItem>
                  <SelectItem value="L">L - Litro</SelectItem>
                  <SelectItem value="CJ">CJ - Conjunto</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Valor Estimado */}
          <div className="space-y-2">
            <Label>Valor Estimado (opcional)</Label>
            <CurrencyInput
              value={formData.valor_estimado}
              onChange={(value) => setFormData(prev => ({ ...prev, valor_estimado: value }))}
              placeholder="0,00"
            />
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label>Descrição / Especificações</Label>
            <Textarea
              value={formData.descricao}
              onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
              placeholder="Detalhes técnicos, especificações, requisitos..."
              rows={3}
            />
          </div>

          {/* Fornecedores */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Fornecedores para Cotar * ({formData.fornecedores_ids.length} selecionados)
            </Label>
            <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
              {fornecedores.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">
                  Nenhum fornecedor cadastrado
                </p>
              ) : (
                fornecedores.map(forn => (
                  <div
                    key={forn.id}
                    onClick={() => toggleFornecedor(forn.id)}
                    className={`
                      p-2 rounded-lg cursor-pointer transition-colors
                      ${formData.fornecedores_ids.includes(forn.id)
                        ? 'bg-blue-100 border-2 border-blue-500'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'}
                    `}
                  >
                    <p className="font-medium text-sm">{forn.razao_social || forn.nome}</p>
                    <p className="text-xs text-gray-500">{forn.email}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Urgente */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="urgente"
              checked={formData.urgente}
              onChange={(e) => setFormData(prev => ({ ...prev, urgente: e.target.checked }))}
              className="rounded"
            />
            <Label htmlFor="urgente" className="cursor-pointer">
              Marcar como <Badge variant="destructive" className="ml-1">URGENTE</Badge>
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Criando...
              </>
            ) : (
              <>
                <ShoppingCart className="h-4 w-4 mr-2" />
                Criar Cotação
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DialogSolicitarCompra;
